/**
 * SearchBar - Reusable search input component
 * Can be used for searching portfolios, clients, securities, etc.
 */

import React, { useState } from 'react';
import { Search, X } from 'lucide-react';

export interface SearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showClearButton?: boolean;
  autoFocus?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value: controlledValue,
  onChange,
  onSearch,
  placeholder = 'Search...',
  className = '',
  size = 'md',
  showClearButton = true,
  autoFocus = false
}) => {
  const [internalValue, setInternalValue] = useState('');
  
  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  
  const sizeConfig = {
    sm: {
      padding: 'pl-8 pr-3 py-1',
      text: 'text-sm',
      icon: 'w-3.5 h-3.5',
      iconLeft: 'left-2.5',
      clearRight: 'right-2'
    },
    md: {
      padding: 'pl-9 pr-4 py-2',
      text: 'text-sm',
      icon: 'w-4 h-4',
      iconLeft: 'left-3',
      clearRight: 'right-3'
    },
    lg: {
      padding: 'pl-11 pr-5 py-2.5',
      text: 'text-base',
      icon: 'w-5 h-5',
      iconLeft: 'left-3.5',
      clearRight: 'right-3.5'
    }
  };
  
  const config = sizeConfig[size];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(value);
    }
  };
  
  const handleClear = () => {
    if (controlledValue === undefined) {
      setInternalValue('');
    }
    onChange?.('');
    onSearch?.('');
  };
  
  return (
    <div className={`relative ${className}`}>
      <Search className={`absolute ${config.iconLeft} top-1/2 transform -translate-y-1/2 text-gray-400 ${config.icon} pointer-events-none`} />
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={`
          ${config.padding}
          ${config.text}
          ${showClearButton && value ? 'pr-8' : ''}
          block w-full
          border border-gray-300 
          rounded-md 
          text-gray-900 
          placeholder-gray-500 
          bg-white
          focus:outline-none 
          focus:ring-2 
          focus:ring-blue-500 
          focus:border-blue-500
          transition-colors
        `}
      />
      {showClearButton && value && (
        <button
          onClick={handleClear}
          className={`absolute ${config.clearRight} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 ${config.icon}`}
          type="button"
        >
          <X className={config.icon} />
        </button>
      )}
    </div>
  );
};

export default SearchBar;